import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormRadioGroup } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import {
  type OrphanOrWidowIncomeFormType,
  getOrphanOrWidowIncomeFormSchema,
} from "Forms/Schemas/Budget/Incomes/orphanOrWidowIncomeFormSchema";
import { toRadioOptions } from "Forms/fieldOptions";

import {
  getGetBudgetOverviewQueryKey,
  getListIncomesQueryKey,
  useCreateIncome,
  useDeleteIncome,
  useGetIncome,
  useUpdateIncome,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Income, IncomeAmountAmountType, IncomeBodyIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  onClose: () => void;
  clientId?: string;
  edit?: Income;
};

export const OrphanOrWidowIncomeForm = forwardRef<{}, Props>(({ onClose, clientId, edit }, ref) => {
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const queryClient = useQueryClient();
  const { data: editIncomeData } = useGetIncome(analysisId, versionId, edit?.guid ?? "", {
    query: { enabled: !!edit },
  });
  const { mutate: putIncome } = useUpdateIncome();
  const { mutate } = useCreateIncome();
  const { mutate: deleteIncome } = useDeleteIncome();

  const { control, handleSubmit, setValue, watch, clearErrors } = useForm<OrphanOrWidowIncomeFormType>({
    resolver: yupResolver(getOrphanOrWidowIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      degree: IncomeAmountAmountType.orphan_pension,
      amountValueOrphan: "" as any,
      amountValueWidow: "" as any,
    },
  });

  useEffect(() => {
    if (editIncomeData) {
      const type = editIncomeData?.incomeAmounts.some((x) => x.amountType === IncomeAmountAmountType.widow_pension)
        ? IncomeAmountAmountType.widow_pension
        : IncomeAmountAmountType.orphan_pension;
      if (type === IncomeAmountAmountType.widow_pension) {
        setValue("degree", IncomeAmountAmountType.widow_pension);
        setValue("amountValueWidow", (editIncomeData?.incomeAmounts[0]?.amount.value.toString() ?? "") as any);
        setValue("amountValueOrphan", "" as any);
      } else {
        setValue("degree", IncomeAmountAmountType.orphan_pension);
        setValue("amountValueOrphan", (editIncomeData?.incomeAmounts[0]?.amount.value.toString() ?? "") as any);
        setValue("amountValueWidow", "" as any);
      }
    }
  }, [editIncomeData, setValue]);

  const selectedDegree = watch("degree");

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteIncome: () => {
      handleDelete();
    },
  }));

  const onSubmit = async (data: OrphanOrWidowIncomeFormType) => {
    const amountValue = data.degree === IncomeAmountAmountType.orphan_pension ? data.amountValueOrphan : data.amountValueWidow;

    if (edit) {
      putIncome(
        {
          analysisId,
          versionId,
          incomeId: edit.guid,
          data: {
            incomeType: IncomeBodyIncomeType.orphan_or_widow_pension,
            clientGuid: clientId ?? "",
            incomeAmounts: [
              {
                amountType: data.degree,
                amount: {
                  value: Number(amountValue) || 0,
                  currencyCode: "CZK",
                  frequency: AmountFrequency.monthly,
                },
                name: "",
              },
            ],
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    } else {
      mutate(
        {
          data: {
            incomeType: IncomeBodyIncomeType.orphan_or_widow_pension,
            clientGuid: clientId ?? "",
            incomeAmounts: [
              {
                amountType: data.degree,
                amount: {
                  value: Number(amountValue) || 0,
                  currencyCode: "CZK",
                  frequency: AmountFrequency.monthly,
                },
                name: "",
              },
            ],
          },
          analysisId,
          versionId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    }
  };

  const handleDelete = () => {
    deleteIncome(
      {
        analysisId,
        versionId,
        incomeId: edit?.guid ?? "",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
        },
      },
    );
  };

  return (
    <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
      <Box>
        <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
          {t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.OrphanOrWidowPension)}
        </Typography>

        <Grid container spacing={Spacing.XL}>
          <Grid size={12}>
            <FormRadioGroup
              control={control}
              name="degree"
              options={toRadioOptions([
                {
                  code: IncomeAmountAmountType.orphan_pension,
                  name: t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.OrphanOrWidowIncome.OrphanPension),
                },
              ])}
              defaultValue={IncomeAmountAmountType.orphan_pension}
              columns={2}
              disabled={readOnly}
              onChange={() => {
                setValue("amountValueWidow", "" as any);
                clearErrors("amountValueWidow");
              }}
            />

            <FormNumberField
              control={control}
              name="amountValueOrphan"
              required
              valueType="text"
              scale={2}
              min={0}
              thousandsSeparator=" "
              unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              disabled={selectedDegree !== IncomeAmountAmountType.orphan_pension || readOnly}
              placeholder="0"
              sx={{
                width: "50%",
              }}
              fullWidth
            />

            <FormRadioGroup
              control={control}
              name="degree"
              options={toRadioOptions([
                {
                  code: IncomeAmountAmountType.widow_pension,
                  name: t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.OrphanOrWidowIncome.WidowPension),
                },
              ])}
              defaultValue={IncomeAmountAmountType.widow_pension}
              columns={2}
              disabled={readOnly}
              onChange={() => {
                setValue("amountValueOrphan", "" as any);
                clearErrors("amountValueOrphan");
              }}
            />

            <FormNumberField
              control={control}
              name="amountValueWidow"
              required
              valueType="text"
              scale={2}
              min={0}
              thousandsSeparator=" "
              unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              disabled={selectedDegree !== IncomeAmountAmountType.widow_pension || readOnly}
              placeholder="0"
              sx={{
                width: "50%",
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
});
